<template>
  <div>
    <main-header>
      <v-spacer />
    </main-header>

    <v-container fluid>
      <v-toolbar
        class="toolbar"
        height="84"
        flat
      >
        <div class="toolbar-content-special d-flex align-center w-100">
          <SelectProject
            v-if="tenantsList && tenantsList.length > 0"
            :tenants-list="tenantsList"
            @update="onChangedSelectedProject"
          />
          <v-spacer />
          <FilterInput
            v-model="filter"
            :items="auditLogRatesTableFilters"
            filter-range-from-payload-string=">"
            filter-range-to-payload-string="<"
          />
        </div>
      </v-toolbar>
      <v-divider />

      <v-data-table
        v-if="isRequiredFilterSelected"
        :headers="auditLogRatesTableHeaders"
        :items="itemsList"
        class="elevation-1 table"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loadingTable"
        hide-default-footer
        item-key="key"
        :items-per-page="perPage"
        fixed-header
        @update:sort-by="getItemsListWithSort"
        @update:sort-desc="getItemsListWithSort"
      >
        <template v-slot:no-data>
          <v-btn
            color="primary"
            dark
            @click="getItemsList"
          >
            Refresh
          </v-btn>
        </template>

        <template v-slot:footer>
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col
                cols="9"
                align-self="center"
              >
                <v-pagination
                  v-model="page"
                  class="b-pagination"
                  :length="totalPages"
                  :total-visible="11"
                  @input="onChangePage"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="perPage"
                  class="select-per-page"
                  :items="perPageItems"
                  placeholder="Items per page"
                  dance
                  @change="perPage = parseInt($event, 10)"
                />
              </v-col>
            </v-row>
          </v-container>
          <div class="text-center py-2" />
        </template>
      </v-data-table>

      <v-alert
        v-else
        density="compact"
        max-width="800"
        style="margin: 2rem auto"
        variant="plain"
        title="Alert title"
        type="warning"
      >
        {{ isRequiredFilterNotSelected }}
      </v-alert>
    </v-container>
  </div>
</template>

<script>
// constants
import {
  auditLogRatesTableHeaders,
  auditLogRatesTableFilters
} from "@/utils/staticTablesColumns";
// modules
import { mapGetters } from "vuex";
// components
import MainHeader from "@/components/MainHeader";
import SelectProject from "@/components/SelectProject";
import FilterInput from "@/components/FilterInput";

export default {
  name: "AuditLogRates",

  components: {
    MainHeader,
    SelectProject,
    FilterInput
  },

  data() {
    return {
      auditLogRatesTableHeaders,
      auditLogRatesTableFilters,

      restoreFromQuery: false,
      sortBy: [],
      sortDesc: [],
      loadingTable: false,
      perPage: 10,
      page: 1,
      filter: [],
      perPageItems: [10, 25, 50, 100, 500]
    }
  },

  computed: {
    ...mapGetters({
      itemsList: "AuditLogStore/auditLogRates",
      pagination: "AuditLogStore/auditLogRatesPagination",
      tenantsList: "RecordStore/getTenants"
    }),

    totalPages() {
      return this.pagination.totalPages || 0;
    },

    filterStringPayload() {
      const filterObject = {};

      this.filter.forEach(item => {
        return filterObject[item.key] = item.value;
      });
      return filterObject
    },

    filterStringURL: {
      get() {
        return this.filter.map(item => {
          return `${item.key}:${item.value}:${item.name}`;
        }).join(",");
      },
      set(newValue) {
        const filterArray = newValue.split(",");

        filterArray.forEach((item, index) => {
          const filterKeyValue = item.split(":")
          this.filter[index] = {};
          this.filter[index].key = filterKeyValue[0];
          this.filter[index].value = filterKeyValue[1];
          this.filter[index].name = filterKeyValue[2] || "";
        });

        return newValue;
      }
    },

    isRequiredFilterSelected () {
      if (
        !!this.filterStringPayload.sendingCountryIso3 &&
        !!this.filterStringPayload.receiveCountryIso3 &&
        !!this.filterStringPayload.receiveCurrency &&
        !!this.filterStringPayload.serviceCode
      ) {
        return true;
      } else {
        return false;
      }
    },

    isRequiredFilterNotSelected () {
      return this.isRequiredFilterSelected ? '' : 'Please select Corridor: Send country, Recieve country, Recieve currency, Payout Method (Example: GBR_SOM_USD_WLT)';
    }
  },


  watch: {
    perPage (newValue) {
      if (this.restoreFromQuery) return;

      this.addQueryToRouteWithReload("perPage", newValue);
      this.addQueryToRouteWithReload("page", 1);
    },
    filter: {
      handler() {
        if (this.restoreFromQuery) return;

        this.getItemsListWithFilters();
      },
      deep: true
    }
  },

  beforeCreate() {
    const query = this.$route.query;
    if (query.tenant) {
      this.$store.dispatch("AppStore/setTenant", query.tenant);
    }
  },

  created() {
    if (!this.$store.getters["RecordStore/getTenants"]) {
      this.$store.dispatch("RecordStore/getTenants");
    }
    this.addQueryToRoute("tenant", this.$store.getters["AppStore/getTenant"]);

    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 10;
      this.page = parseInt(query.page) || 1;
      let sort = query.sort || [];

      if (sort.length) {
        if (typeof sort === "string") sort = [sort];

        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] !== "asc");
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }

      if (query?.filter?.length) {
        this.filterStringURL = query.filter;
      }
    }

    this.getItemsList();
  },

  methods: {
    async getItemsList () {
      try {
        this.loadingTable = true;

        const payload = {
          page: this.page,
          size: this.perPage,
          sort: this.sortBy.map(
            (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
          ),
          ...this.filterStringPayload,
        };

        if (this.isRequiredFilterSelected) {
          await this.$store.dispatch("AuditLogStore/getAuditLogRates", payload)
        } else {
          return;
        }

      } finally {
        this.loadingTable = false;
        this.restoreFromQuery = false;
      }
    },

    getItemsListWithSort () {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.addQueryToRouteWithReload("page", 1);
      this.addQueryToRouteWithReload("sort", sort);
    },

    getListAndGoToFirstPage () {
      if (this.loadingTable) return;
      this.page = 1;
      this.getItemsList();
    },

    onChangedSelectedProject () {
      this.addQueryToRoute("tenant", this.$store.getters["AppStore/getTenant"]);
      this.getListAndGoToFirstPage();
    },

    addQueryToRoute(name, value) {
      this.$router.replace(
        { query: Object.assign({ ...this.$route.query }, { [name]: value }) },
        () => {}
      );
    },

    addQueryToRouteWithReload (name, value) {
      this.$router.replace({
        query: Object.assign({
          ...this.$route.query
        }, {
          [name]: value
        })},
      () => {}
      );
    },

    getItemsListWithFilters () {
      this.addQueryToRouteWithReload("filter", this.filterStringURL);
      this.addQueryToRouteWithReload("perPage", this.perPage);
      this.addQueryToRouteWithReload("page", 1);
    },

    onChangePage () {
      this.addQueryToRouteWithReload("page", this.page);
    }
  }
}
</script>
